import React, { useEffect, useState } from "react";
import { ArrowLeft, ShieldAlert, MapPin, ImageIcon, Mail, Lock, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { resolveLocationLabel } from "../../lib/locationLabel";

const summarizeAttachments = (atts) => {
  if (!atts || atts.length === 0) return "Aucune preuve jointe";
  const n = (k) => atts.filter((a) => a.kind === k).length;
  const parts = [];
  const p = n("image"); if (p) parts.push(p === 1 ? "1 photo" : `${p} photos`);
  const v = n("video"); if (v) parts.push(v === 1 ? "1 vidéo" : `${v} vidéos`);
  const a = n("audio"); if (a) parts.push(a === 1 ? "1 audio" : `${a} audios`);
  return parts.join(", ");
};

const Row = ({ icon: Icon, iconBg, iconColor, label, value, valueColor, check, checkColor, testid }) => (
  <div className="flex items-center gap-3 py-3" data-testid={testid}>
    <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: iconBg }}>
      <Icon className="w-5 h-5" style={{ color: iconColor }} strokeWidth={2.4} />
    </span>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold leading-tight">
        {label}
      </div>
      <div className="text-[15px] font-bold truncate leading-tight mt-0.5"
           style={valueColor ? { color: valueColor } : {}}>
        {value}
      </div>
    </div>
    {check && (
      <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
            style={{ background: checkColor }}>
        <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} />
      </span>
    )}
  </div>
);

/**
 * "Aperçu de l'alerte" — fullscreen review screen.
 * The ONLY place that fires `onSend`. Disables the button on click +
 * shows a spinner so multiple taps cannot create duplicates.
 */
export const HomeReviewScreen = ({
  open, onBack, onSend, types, typeLabels, attachments, coords, submitting,
}) => {
  const [posLabel, setPosLabel] = useState("Position détectée");
  const [posKnown, setPosKnown] = useState(false);

  useEffect(() => {
    if (!open || !coords) return undefined;
    let cancelled = false;
    resolveLocationLabel(coords.latitude, coords.longitude).then((label) => {
      if (cancelled) return;
      if (label) {
        setPosLabel(label);
        setPosKnown(true);
      } else {
        setPosLabel(`Position détectée (${coords.latitude.toFixed(3)}, ${coords.longitude.toFixed(3)})`);
        setPosKnown(true);
      }
    });
    return () => { cancelled = true; };
  }, [open, coords]);

  if (!open) return null;

  const catLabel = types.map((ty) => typeLabels[ty]?.label || ty).join(" / ");

  return (
    <div
      className="fixed inset-0 z-[60] bg-background overflow-y-auto"
      data-testid="review-screen"
      role="dialog"
      aria-modal="true"
    >
      <header className="px-4 pb-3 flex items-center gap-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={onBack}
          data-testid="review-back-btn"
          aria-label="Retour"
          disabled={submitting}
          className="w-9 h-9 rounded-lg hover:bg-muted flex items-center justify-center disabled:opacity-40"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 text-center pr-9">
          <h2 className="font-display text-lg font-black tracking-tight text-mali-navy dark:text-white leading-tight">
            Aperçu de l&apos;alerte
          </h2>
          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
            Vérifiez les informations avant d&apos;envoyer votre alerte
          </p>
        </div>
      </header>

      <main className="px-4 pt-2 space-y-4 max-w-md mx-auto pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <section className="bg-card border border-border rounded-2xl px-4 py-1 divide-y divide-border/60" data-testid="review-summary-card">
          <Row
            icon={ShieldAlert} iconBg="rgba(206,17,38,0.10)" iconColor="#CE1126"
            label="Catégorie" value={catLabel || "—"} valueColor="#CE1126"
            testid="review-row-category"
          />
          <Row
            icon={MapPin}
            iconBg="rgba(20,181,58,0.10)" iconColor="#14B53A"
            label="Position GPS"
            value={posLabel}
            valueColor="#14B53A"
            check={posKnown}
            checkColor="#14B53A"
            testid="review-row-position"
          />
          <Row
            icon={ImageIcon}
            iconBg="rgba(37,99,235,0.10)" iconColor="#2563EB"
            label="Preuves jointes"
            value={summarizeAttachments(attachments)}
            valueColor="#2563EB"
            check={attachments.length > 0}
            checkColor="#2563EB"
            testid="review-row-evidence"
          />
        </section>

        <div className="bg-mali-yellow/15 border border-mali-yellow/40 rounded-2xl px-4 py-3 flex gap-3 items-start" data-testid="review-info-box">
          <ShieldCheck className="w-5 h-5 text-amber-700 dark:text-mali-yellow shrink-0 mt-0.5" strokeWidth={2.4} />
          <p className="text-[12px] text-amber-900 dark:text-mali-yellow leading-snug">
            Votre alerte sera envoyée immédiatement aux services d&apos;urgence les plus proches.
          </p>
        </div>

        <button
          type="button"
          onClick={onSend}
          disabled={submitting}
          data-testid="send-alert-now-btn"
          className="w-full bg-mali-red text-white font-display font-black text-base tracking-wide py-5 rounded-2xl shadow-lg shadow-mali-red/30 flex items-center justify-center gap-3 hover:opacity-95 active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>ENVOI EN COURS…</span>
            </>
          ) : (
            <>
              <Mail className="w-5 h-5" strokeWidth={2.5} />
              <span>ENVOYER L&apos;ALERTE MAINTENANT</span>
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground pt-1">
          <Lock className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.5} />
          <span>Vos données sont sécurisées</span>
        </div>
      </main>
    </div>
  );
};
